import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Lock, Loader2, ArrowRight, Target, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

const GOALS = [
  { key: "get_job", label: "Get a Job", blurb: "Land interviews & offers fast." },
  { key: "start_business", label: "Start a Business", blurb: "Idea → first paying customer." },
  { key: "side_hustle", label: "Start a Side Hustle", blurb: "Earn £200-£500/month extra." },
  { key: "freelance", label: "Become a Freelancer", blurb: "Land your first 3 clients." },
  { key: "get_customers", label: "Get More Customers", blurb: "Grow your existing business." },
];

export default function FreeRoadmapPage() {
  const nav = useNavigate();
  const [goal, setGoal] = useState("side_hustle");
  const [form, setForm] = useState({ email: "", target: "", country: "United Kingdom", budget: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!form.email.includes("@")) { toast.error("Enter a valid email to receive your roadmap"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/leads/roadmap", {
        email: form.email,
        goal,
        target: form.target || undefined,
        country: form.country || undefined,
        budget: form.budget || undefined,
      });
      setResult(data);
      window.localStorage.setItem("zuntria_lead_id", data.lead_id);
      window.localStorage.setItem("zuntria_lead_email", form.email);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate your roadmap");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-12 md:px-10">
      {!result ? (
        <>
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs text-accent" data-testid="free-roadmap-badge">
              <Sparkles className="h-3 w-3" /> 100% free — no account needed
            </div>
            <h1 className="mt-5 font-display text-4xl font-medium tracking-tighter sm:text-5xl">
              Get your personalised <span className="text-accent">success roadmap</span> in 60 seconds
            </h1>
            <p className="mt-4 text-muted-foreground">
              Tell our AI your goal. It builds you a real, day-by-day action plan — tailored to your situation, skills and budget.
            </p>
          </div>

          <div className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="free-roadmap-form">
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">1 · Pick your goal</Label>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {GOALS.map((g) => (
                <button key={g.key} onClick={() => setGoal(g.key)} data-testid={`fr-goal-${g.key}`}
                  className={`rounded-xl border p-4 text-left transition-colors ${goal === g.key ? "border-accent bg-accent/10" : "border-border bg-background hover:border-accent/40"}`}>
                  <div className="flex items-center gap-2 text-sm font-medium"><Target className="h-4 w-4 text-accent" /> {g.label}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{g.blurb}</div>
                </button>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>2 · Your specific target (optional but powerful)</Label>
                <Textarea rows={2} className="mt-1.5" value={form.target}
                  onChange={(e) => setForm((s) => ({ ...s, target: e.target.value }))}
                  placeholder="e.g. Earn £500/month doing car valeting in Manchester"
                  data-testid="fr-target-input" />
              </div>
              <div>
                <Label>Country</Label>
                <Input className="mt-1.5" value={form.country}
                  onChange={(e) => setForm((s) => ({ ...s, country: e.target.value }))}
                  data-testid="fr-country-input" />
              </div>
              <div>
                <Label>Budget (optional)</Label>
                <Input className="mt-1.5" value={form.budget}
                  onChange={(e) => setForm((s) => ({ ...s, budget: e.target.value }))}
                  placeholder="e.g. £100" data-testid="fr-budget-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>3 · Where should we send it?</Label>
                <Input type="email" className="mt-1.5" value={form.email}
                  onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                  placeholder="your@email.com" data-testid="fr-email-input" />
              </div>
            </div>

            <Button onClick={generate} disabled={busy} size="lg"
              className="mt-6 w-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="fr-generate-btn">
              {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is building your roadmap… (~30s)</> : <>Build my free roadmap <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground">No card. No spam. Just your plan.</p>
          </div>
        </>
      ) : (
        <div data-testid="fr-result">
          <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs text-accent">
            <Map className="h-3 w-3" /> Your roadmap · {result.goal_label}
          </div>
          <h1 className="mt-4 font-display text-3xl font-medium tracking-tight sm:text-4xl">Your personalised plan is ready 🎉</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">{result.summary}</p>

          <div className="mt-8 space-y-3">
            {result.preview_tasks.map((t) => (
              <div key={t.task_id} className="rounded-2xl border border-accent/30 bg-card p-5" data-testid={`fr-task-${t.task_id}`}>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-accent">Day {t.day}</span>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">+{t.points}pt</span>
                </div>
                <h3 className="mt-2 font-display text-lg">{t.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t.description}</p>
              </div>
            ))}

            {result.locked_tasks.length > 0 && (
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card" data-testid="fr-locked-section">
                <div className="space-y-3 p-5 blur-[3px] select-none" aria-hidden>
                  {result.locked_tasks.map((t, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Day {t.day}</span>
                      <span className="text-sm">{t.title}</span>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/70 p-6 text-center backdrop-blur-[2px]">
                  <Lock className="h-6 w-6 text-accent" />
                  <p className="font-display text-lg">{result.locked_tasks.length} more days locked</p>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Create your free account to unlock the full roadmap, daily tracking, your AI coach and achievements.
                  </p>
                  <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90"
                    onClick={() => nav("/signup")} data-testid="fr-unlock-btn">
                    Unlock my full roadmap — free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary hover:underline">Log in</Link> and your roadmap activates automatically.
          </p>
        </div>
      )}
    </div>
  );
}
